import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/creatorChannelAlbumsService";

const DEFAULT_ALBUMS_LIMIT = 12;
const DEFAULT_PHOTOS_LIMIT = 24;

const normalizeAlbums = (items) => {
  if (!Array.isArray(items)) return [];

  return items.filter(Boolean).map((album) => ({
    ...album,
    albumId: album.albumId ?? album.album_id,
    albumTag: album.albumTag ?? album.album_tag,
    title: album.title || "Untitled Album",
    thumbnailUrl: album.thumbnailUrl ?? album.thumbnail_url ?? null,
  }));
};

const normalizePhotos = (items) => {
  if (!Array.isArray(items)) return [];

  return items.filter(Boolean).map((photo) => ({
    ...photo,
    imageId: photo.imageId ?? photo.image_id,
    imageTag: photo.imageTag ?? photo.image_tag,
    imageUrl: photo.imageUrl ?? photo.image_url ?? photo.url ?? null,
    thumbnailUrl: photo.thumbnailUrl ?? photo.thumbnail_url ?? null,
  }));
};

const mergeUnique = (current, incoming, getKey) => {
  const map = new Map();

  for (const item of [...current, ...incoming]) {
    const key = getKey(item);

    if (key != null) {
      map.set(String(key), item);
    }
  }

  return [...map.values()];
};

const getHasMore = (pagination, offset, receivedCount, limit) => {
  if (typeof pagination?.hasMore === "boolean") {
    return pagination.hasMore;
  }

  const total = Number(pagination?.total);

  if (pagination?.total != null && Number.isFinite(total)) {
    return offset < total;
  }

  return receivedCount === limit;
};

export const useCreatorChannelAlbums = ({
  username,
  albumId = null,
  limit = DEFAULT_ALBUMS_LIMIT,
  photosLimit = DEFAULT_PHOTOS_LIMIT,
} = {}) => {
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosLoadingMore, setPhotosLoadingMore] = useState(false);

  const [error, setError] = useState("");
  const [photosError, setPhotosError] = useState("");

  const [hasMore, setHasMore] = useState(false);
  const [photosHasMore, setPhotosHasMore] = useState(false);

  const albumsRequestIdRef = useRef(0);
  const photosRequestIdRef = useRef(0);

  const albumsFetchingRef = useRef(false);
  const photosFetchingRef = useRef(false);

  const albumsRef = useRef([]);
  const photosRef = useRef([]);

  const albumsOffsetRef = useRef(0);
  const photosOffsetRef = useRef(0);

  const creatorUsername = String(username || "").trim();

  const parsedLimit = Math.min(
    Math.max(Number.parseInt(limit, 10) || DEFAULT_ALBUMS_LIMIT, 1),
    50,
  );

  const parsedPhotosLimit = Math.min(
    Math.max(Number.parseInt(photosLimit, 10) || DEFAULT_PHOTOS_LIMIT, 1),
    100,
  );

  const fetchAlbums = useCallback(
    async ({ reset = false } = {}) => {
      if (!creatorUsername) return false;

      if (albumsFetchingRef.current && !reset) {
        return false;
      }

      const requestId = ++albumsRequestIdRef.current;
      const offset = reset ? 0 : albumsOffsetRef.current;

      albumsFetchingRef.current = true;

      if (reset) {
        setLoading(true);
        setLoadingMore(false);
        setHasMore(false);
        albumsRef.current = [];
        albumsOffsetRef.current = 0;
        setAlbums([]);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = await service.getCreatorChannelAlbums(
          creatorUsername,
          {
            limit: parsedLimit,
            offset,
          },
        );

        if (requestId !== albumsRequestIdRef.current) {
          return false;
        }

        const data = response?.data ?? response ?? {};

        if (data.success === false) {
          throw new Error(data.message || "Unable to load albums.");
        }

        const received = normalizeAlbums(
          data.albums ?? data.data?.albums ?? data.items,
        );

        const nextAlbums = mergeUnique(
          reset ? [] : albumsRef.current,
          received,
          (album) => album.albumId ?? album.albumTag,
        );

        const nextOffset = offset + received.length;

        albumsRef.current = nextAlbums;
        albumsOffsetRef.current = nextOffset;

        setAlbums(nextAlbums);
        setHasMore(
          getHasMore(
            data.pagination ?? data.data?.pagination,
            nextOffset,
            received.length,
            parsedLimit,
          ),
        );

        return received;
      } catch (err) {
        if (requestId !== albumsRequestIdRef.current) {
          return false;
        }

        console.error("getCreatorChannelAlbums failed:", err);

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load creator albums.",
        );

        if (reset) {
          setHasMore(false);
        }

        return false;
      } finally {
        if (requestId === albumsRequestIdRef.current) {
          albumsFetchingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [creatorUsername, parsedLimit],
  );

  const fetchPhotos = useCallback(
    async ({ reset = false } = {}) => {
      if (!creatorUsername || albumId == null) {
        return false;
      }

      if (photosFetchingRef.current && !reset) {
        return false;
      }

      const requestId = ++photosRequestIdRef.current;
      const offset = reset ? 0 : photosOffsetRef.current;

      photosFetchingRef.current = true;

      if (reset) {
        setPhotosLoading(true);
        setPhotosLoadingMore(false);
        setPhotosHasMore(false);
        photosRef.current = [];
        photosOffsetRef.current = 0;
        setPhotos([]);
      } else {
        setPhotosLoadingMore(true);
      }

      setPhotosError("");

      try {
        const response = await service.getCreatorChannelAlbumPhotos(
          creatorUsername,
          albumId,
          {
            limit: parsedPhotosLimit,
            offset,
          },
        );

        if (requestId !== photosRequestIdRef.current) {
          return false;
        }

        const data = response?.data ?? response ?? {};

        if (data.success === false) {
          throw new Error(data.message || "Unable to load photos.");
        }

        const received = normalizePhotos(
          data.photos ??
            data.images ??
            data.data?.photos ??
            data.data?.images ??
            data.items,
        );

        const nextPhotos = mergeUnique(
          reset ? [] : photosRef.current,
          received,
          (photo) => photo.imageId ?? photo.imageTag,
        );

        const nextOffset = offset + received.length;

        photosRef.current = nextPhotos;
        photosOffsetRef.current = nextOffset;

        setPhotos(nextPhotos);
        setPhotosHasMore(
          getHasMore(
            data.pagination ?? data.data?.pagination,
            nextOffset,
            received.length,
            parsedPhotosLimit,
          ),
        );

        return received;
      } catch (err) {
        if (requestId !== photosRequestIdRef.current) {
          return false;
        }

        console.error("getCreatorChannelAlbumPhotos failed:", err);

        setPhotosError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load album photos.",
        );

        if (reset) {
          setPhotosHasMore(false);
        }

        return false;
      } finally {
        if (requestId === photosRequestIdRef.current) {
          photosFetchingRef.current = false;
          setPhotosLoading(false);
          setPhotosLoadingMore(false);
        }
      }
    },
    [creatorUsername, albumId, parsedPhotosLimit],
  );

  useEffect(() => {
    albumsRequestIdRef.current += 1;
    albumsFetchingRef.current = false;

    albumsRef.current = [];
    albumsOffsetRef.current = 0;

    setAlbums([]);
    setHasMore(false);
    setError("");

    if (creatorUsername) {
      void fetchAlbums({ reset: true });
    } else {
      setLoading(false);
      setLoadingMore(false);
    }

    return () => {
      albumsRequestIdRef.current += 1;
      albumsFetchingRef.current = false;
    };
  }, [creatorUsername, fetchAlbums]);

  useEffect(() => {
    photosRequestIdRef.current += 1;
    photosFetchingRef.current = false;

    photosRef.current = [];
    photosOffsetRef.current = 0;

    setPhotos([]);
    setPhotosHasMore(false);
    setPhotosError("");

    if (creatorUsername && albumId != null) {
      void fetchPhotos({ reset: true });
    } else {
      setPhotosLoading(false);
      setPhotosLoadingMore(false);
    }

    return () => {
      photosRequestIdRef.current += 1;
      photosFetchingRef.current = false;
    };
  }, [creatorUsername, albumId, fetchPhotos]);

  const loadMore = useCallback(() => {
    if (albumsFetchingRef.current || loading || loadingMore || !hasMore) {
      return Promise.resolve(false);
    }

    return fetchAlbums({ reset: false });
  }, [fetchAlbums, loading, loadingMore, hasMore]);

  const loadMorePhotos = useCallback(() => {
    if (
      photosFetchingRef.current ||
      photosLoading ||
      photosLoadingMore ||
      !photosHasMore
    ) {
      return Promise.resolve(false);
    }

    return fetchPhotos({ reset: false });
  }, [fetchPhotos, photosLoading, photosLoadingMore, photosHasMore]);

  return {
    albums,
    photos,
    loading,
    loadingMore,
    photosLoading,
    photosLoadingMore,
    error,
    photosError,
    hasMore,
    photosHasMore,
    refresh: () => fetchAlbums({ reset: true }),
    loadMore,
    refreshPhotos: () => fetchPhotos({ reset: true }),
    loadMorePhotos,
  };
};

export default useCreatorChannelAlbums;
